import { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { IoMdArrowRoundBack } from "react-icons/io";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { getSites } from "../../../Services/siteService";
import { createTask } from "../../../Services/taskServices";
import { getAllUsers } from "../../../Services/UserService";
import { GET_ALL_PAGINATION } from "../../../Constants/Strings";
import { TASK_PAGE } from "../../../routes";
import { Loader } from "../../../Components/Common";
import { UserContext } from "../../../Context";
import { USERDATA_ERROR } from "../../Attendance/constants";
import { REQUIRED, SUCCESS, ERROR } from "../constant";
import { extractErrorMessage } from "../../../Utils/extractErrorMessage";

const AddTask = () => {
  const [loading, setLoading] = useState(false);
  const [usersData, setUsersData] = useState([]);
  const [sitesData, setSitesData] = useState([]);
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  const validationSchema = Yup.object({
    title: Yup.string().required(REQUIRED.TITLE),
    description: Yup.string().required(REQUIRED.DESCRIPTION),
    start_date: Yup.date()
      .min(
        new Date(new Date().setHours(0, 0, 0, 0)),
        "Start Date cannot be in the past"
      )
      .required(REQUIRED.START_DATE),
    estimated_end_date: Yup.date()
      .min(
        new Date(new Date().setHours(0, 0, 0, 0)),
        "Estimated End Date cannot be in the past"
      )
      .required(REQUIRED.ESTIMATED_END_DATE),
    assignee:
      user.role === "SITESTAFF" ? "" : Yup.string().required(REQUIRED.ASSIGNEE),
    site: Yup.string().required(REQUIRED.SITE),
  });

  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      try {
        const [userRes, siteRes] = await Promise.all([
          getAllUsers(GET_ALL_PAGINATION),
          getSites(GET_ALL_PAGINATION),
        ]);
        if (userRes.status === 200)
          setUsersData(userRes.data.results || userRes.data);
        else toast.error(USERDATA_ERROR);

        if (siteRes.status === 200)
          setSitesData(siteRes.data.results || siteRes.data.results);
        else toast.error(ERROR.SITE);
      } catch (error) {
        toast.error(ERROR.DATA);
      } finally {
        setLoading(false);
      }
    };
    fetchAllData();
  }, []);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setLoading(true);
    try {
      const response = await createTask(values);
      if (response.status === 201) {
        toast.success(SUCCESS.TASK_CREATED);
        resetForm();
        navigate(-1);
      } else {
        const msg = extractErrorMessage(response) || ERROR.TASK;
        toast.error(msg);
      }
    } catch (error) {
      const msg = extractErrorMessage(error) || ERROR.TASK;
      toast.error(msg);
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const initialValues = {
    title: "",
    description: "",
    start_date: today,
    estimated_end_date: today,
    [user.role === "SITESTAFF" ? "" : "assignee"]: user?.id?.toString(),
    site: "",
  };

  return (
    <div className="flex items-center justify-center">
      {loading ? (
        <Loader />
      ) : (
        <div className="bg-white w-full max-w-md mx-4 rounded-2xl shadow-xl p-6 relative mt-6">
          <div className="flex justify-center items-center mb-4">
            <button
              type="button"
              onClick={() => navigate(TASK_PAGE)}
              className="absolute left-0 text-primary text-xl hover:text-secondary ml-6"
            >
              <IoMdArrowRoundBack />
            </button>
            <h2 className="text-xl font-semibold text-center text-primary">
              Add Task
            </h2>
          </div>

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            validateOnChange={true}
            validateOnBlur={false}
          >
            {({ values, handleChange }) => (
              <Form className="space-y-3">
                {/* Title */}
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700">
                    Task Title
                  </label>
                  <Field
                    name="title"
                    type="text"
                    placeholder="Enter the task"
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-primary focus:outline-none"
                  />
                  <ErrorMessage
                    name="title"
                    component="div"
                    className="text-red-500 text-xs"
                  />
                </div>

                {/* Description */}
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <Field
                    as="textarea"
                    name="description"
                    placeholder="Enter the description"
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-primary focus:outline-none resize-none"
                  />
                  <ErrorMessage
                    name="description"
                    component="div"
                    className="text-red-500 text-xs"
                  />
                </div>

                {/* Start Date */}
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700">
                    Start Date
                  </label>
                  <Field
                    type="date"
                    name="start_date"
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-primary focus:outline-none"
                  />
                  <ErrorMessage
                    name="start_date"
                    component="div"
                    className="text-red-500 text-xs"
                  />
                </div>

                {/* End Date */}
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700">
                    Estimated End Date
                  </label>
                  <Field
                    type="date"
                    name="estimated_end_date"
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-primary focus:outline-none"
                  />
                  <ErrorMessage
                    name="estimated_end_date"
                    component="div"
                    className="text-red-500 text-xs"
                  />
                </div>

                {/* Assignee */}
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700">
                    Assignee
                  </label>

                  {user.role === "SITESTAFF" ? (
                    <>
                      <input
                        type="text"
                        value={`${user?.first_name} ${user?.last_name}`}
                        disabled
                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none"
                      />
                      <Field
                        type="hidden"
                        name="assignee"
                        value={user?.id?.toString()}
                      />
                      <ErrorMessage
                        name="assignee"
                        component="div"
                        className="text-red-500 text-xs"
                      />
                    </>
                  ) : (
                    <>
                      <Field
                        as="select"
                        name="assignee"
                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-primary focus:outline-none"
                      >
                        <option value="">Select user</option>
                        {usersData?.map((user) => (
                          <option key={user.id} value={user.id}>
                            {user.first_name} {user.last_name}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage
                        name="assignee"
                        component="div"
                        className="text-red-500 text-xs"
                      />
                    </>
                  )}
                </div>

                {/* Site */}
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700">
                    Site
                  </label>
                  <Field
                    as="select"
                    name="site"
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-primary focus:outline-none"
                  >
                    <option value="">Select site</option>
                    {sitesData.map((site) => (
                      <option key={site.id} value={site.id}>
                        {site.name}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage
                    name="site"
                    component="div"
                    className="text-red-500 text-xs"
                  />
                </div>

                {/* Buttons */}
                <div className="flex justify-between gap-3 pt-2">
                  <button
                    type="button"
                    className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-sm text-gray-800"
                    onClick={() => navigate(-1)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 rounded-lg bg-primary hover:bg-green-600 text-white text-sm"
                  >
                    Submit
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      )}
    </div>
  );
};

export default AddTask;
